import { useState, useRef, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, Typography, Box, Chip, LinearProgress,
  Collapse, Alert, Divider, CircularProgress, Tooltip, FormControl,
  InputLabel, Select, MenuItem
} from '@mui/material';
import {
  Close as CloseIcon,
  FolderOpen as FolderOpenIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  FolderZip as ZipIcon,
  Upload as UploadIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import FolderTreeSelect from '../FolderTreeSelect';

// ─── Constantes ───────────────────────────────────────────────────────────────

const ALLOWED_EXT = ['pdf','doc','docx','jpg','jpeg','png','txt','csv','xls','xlsx'];
const MAX_FILE_SIZE = 50 * 1024 * 1024;

function isAllowed(filename) {
  return ALLOWED_EXT.includes(filename.split('.').pop()?.toLowerCase() || '');
}
function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1048576) return `${(bytes/1024).toFixed(1)}KB`;
  return `${(bytes/1048576).toFixed(1)}MB`;
}
function getMime(ext) {
  return ({
    pdf:'application/pdf', doc:'application/msword',
    docx:'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    jpg:'image/jpeg', jpeg:'image/jpeg', png:'image/png',
    txt:'text/plain', csv:'text/csv',
    xls:'application/vnd.ms-excel',
    xlsx:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })[ext] || 'application/octet-stream';
}

// ─── Preview tree ─────────────────────────────────────────────────────────────

function buildPreview(entries) {
  const root = { name:'', children:{}, files:[] };
  for (const e of entries) {
    const parts = e.path.split('/').filter(Boolean);
    let node = root;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!node.children[parts[i]])
        node.children[parts[i]] = { name:parts[i], children:{}, files:[] };
      node = node.children[parts[i]];
    }
    node.files.push(e);
  }
  return root;
}

function countInNode(node) {
  return node.files.length + Object.values(node.children).reduce((s,c) => s + countInNode(c), 0);
}

function PreviewNode({ node, depth=0, officeColor, status }) {
  const [open, setOpen] = useState(true);
  const isRoot = depth === 0;
  const kids = Object.values(node.children).sort((a,b) => a.name.localeCompare(b.name));

  return (
    <Box>
      {!isRoot && (
        <Box onClick={() => setOpen(o=>!o)}
          sx={{ display:'flex', alignItems:'center', gap: 0.75, py: 0.4,
            pl:`${depth*14}px`, cursor:'pointer',
            '&:hover':{ backgroundColor:'#f5f5f5' }, borderRadius: 0.5 }}>
          <IconButton size="small" sx={{ p:0.2, color: officeColor }}>
            {open ? <ExpandMoreIcon sx={{ fontSize:15 }}/> : <ChevronRightIcon sx={{ fontSize:15 }}/>}
          </IconButton>
          {open ? <FolderOpenIcon sx={{ color: officeColor, fontSize:17 }}/> : <FolderIcon sx={{ color: officeColor, fontSize:17 }}/>}
          <Typography variant="body2" fontWeight={600} sx={{ fontSize:'0.8rem' }}>{node.name}</Typography>
          <Chip label={countInNode(node)} size="small"
            sx={{ height:14, fontSize:'0.58rem', backgroundColor:`${officeColor}15`, color: officeColor }}/>
        </Box>
      )}
      <Collapse in={isRoot || open}>
        {kids.map(c => <PreviewNode key={c.name} node={c} depth={depth+1} officeColor={officeColor} status={status}/>)}
        {node.files.map(e => {
          const s = status[e.path];
          return (
            <Box key={e.path} sx={{ display:'flex', alignItems:'center', gap: 0.75, py: 0.35,
              pl:`${(isRoot?0:depth+1)*14+18}px` }}>
              <FileIcon sx={{ fontSize:15, color:'#90a4ae' }}/>
              <Typography variant="body2" sx={{ flex:1, fontSize:'0.78rem' }} noWrap>{e.filename}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mr:1, fontSize:'0.68rem' }}>
                {formatSize(e.size)}
              </Typography>
              {s==='uploading' && <CircularProgress size={13}/>}
              {s==='success'   && <CheckCircleIcon sx={{ fontSize:15, color:'#4caf50' }}/>}
              {s==='error'     && <Tooltip title={e.errorMsg||'Error'}><ErrorIcon sx={{ fontSize:15, color:'#f44336' }}/></Tooltip>}
              {s==='skipped'   && <Chip label="omitido" size="small" sx={{ height:13, fontSize:'0.55rem', color:'#9e9e9e' }}/>}
              {!e.allowed      && !s && <Chip label="tipo no permitido" size="small" sx={{ height:13, fontSize:'0.55rem', color:'#bdbdbd' }}/>}
            </Box>
          );
        })}
      </Collapse>
    </Box>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function ZipUploadModal({ open, onClose, caseData, office, currentUser, flatFolders = [], onComplete }) {
  const [dragActive, setDragActive] = useState(false);
  const [entries,    setEntries]    = useState(null);
  const [preview,    setPreview]    = useState(null);
  const [status,     setStatus]     = useState({});
  const [progress,   setProgress]   = useState({ done:0, total:0 });
  const [uploading,  setUploading]  = useState(false);
  const [done,       setDone]       = useState(false);
  const [warning,    setWarning]    = useState('');
  const [zipName,    setZipName]    = useState('');

  // Carpeta raíz destino (null = raíz del expediente)
  const [rootFolderId, setRootFolderId] = useState(null);

  const fileRef    = useRef(null);
  const officeColor = office?.color || '#1976d2';

  // ── Cargar JSZip ──────────────────────────────────────────────────────────

  const loadJSZip = async () => {
    if (window.JSZip) return window.JSZip;
    return new Promise((res, rej) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
      s.onload = () => res(window.JSZip);
      s.onerror = () => rej(new Error('No se pudo cargar JSZip'));
      document.head.appendChild(s);
    });
  };

  // ── Parsear ZIP ───────────────────────────────────────────────────────────

  const parseZip = async (file) => {
    setWarning(''); setEntries(null); setPreview(null); setStatus({}); setDone(false);
    setZipName(file.name);
    try {
      const JSZip = await loadJSZip();
      const zip   = await JSZip.loadAsync(file);
      const list  = [];

      zip.forEach((path, entry) => {
        if (entry.dir) return;
        const filename = path.split('/').pop();
        if (!filename || filename.startsWith('.') || filename.includes('__MACOSX')) return;
        const folderPath = path.includes('/')
          ? path.substring(0, path.lastIndexOf('/')).replace(/^\//, '')
          : '';
        list.push({
          path, filename, folderPath,
          size:     entry._data?.uncompressedSize || 0,
          allowed:  isAllowed(filename),
          zipEntry: entry
        });
      });

      if (!list.length) { setWarning('El ZIP no contiene archivos válidos.'); return; }

      const skipped = list.filter(e => !e.allowed).length;
      if (skipped) setWarning(`${skipped} archivo(s) serán omitidos por tipo no permitido (permitidos: ${ALLOWED_EXT.join(', ')})`);

      setEntries(list);
      setPreview(buildPreview(list));
    } catch (err) {
      setWarning('Error al leer el ZIP: ' + err.message);
    }
  };

  // ── Drag & Drop zona ──────────────────────────────────────────────────────

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    const f = e.dataTransfer.files[0];
    if (f && (f.name.endsWith('.zip') || f.type.includes('zip'))) parseZip(f);
    else setWarning('Solo se aceptan archivos .zip');
  };
  const handleSelect = (e) => { const f = e.target.files[0]; if (f) parseZip(f); if (fileRef.current) fileRef.current.value=''; };

  // ── Resolver folder_id en BD para una ruta dada ───────────────────────────
  // Crea las carpetas intermedias en Supabase si no existen.
  // folderCache: { 'parentId|nombre' → folder_id } para evitar duplicar INSERTs.

  const resolveFolderId = async (folderPath, rootId, folderCache) => {
    if (!folderPath) return rootId ?? null;

    const parts = folderPath.split('/').filter(Boolean);
    let currentId = rootId ?? null;

    for (const part of parts) {
      const cacheKey = `${currentId ?? 'root'}|${part}`;
      if (folderCache[cacheKey]) {
        currentId = folderCache[cacheKey];
        continue;
      }

      // Buscar si ya existe en BD
      let query = supabase.from('document_folder')
        .select('id')
        .eq('case_id', caseData.id)
        .eq('name', part);
      if (currentId) query = query.eq('parent_id', currentId);
      else           query = query.is('parent_id', null);

      const { data: existing } = await query.maybeSingle();

      if (existing) {
        currentId = existing.id;
      } else {
        const { data: created, error } = await supabase
          .from('document_folder')
          .insert({ case_id: caseData.id, name: part, parent_id: currentId ?? null, created_by: currentUser?.id ?? null })
          .select('id')
          .single();
        if (error) throw new Error(`Error creando carpeta "${part}": ${error.message}`);
        currentId = created.id;
      }

      folderCache[cacheKey] = currentId;
    }

    return currentId;
  };

  // ── Upload ────────────────────────────────────────────────────────────────

  const handleUpload = async () => {
    if (!entries || uploading) return;
    const allowed = entries.filter(e => e.allowed && e.size <= MAX_FILE_SIZE);
    const skipped = entries.filter(e => !e.allowed || e.size > MAX_FILE_SIZE);

    const initStatus = {};
    for (const e of skipped) initStatus[e.path] = 'skipped';
    setStatus(initStatus);
    setProgress({ done:0, total: allowed.length });
    setUploading(true); setDone(false);

    const { data:{ session } } = await supabase.auth.getSession();
    const folderCache = {}; // evitar crear la misma carpeta dos veces
    let ok=0, fail=0;

    for (let i=0; i<allowed.length; i++) {
      const e = allowed[i];
      setStatus(p => ({ ...p, [e.path]:'uploading' }));
      try {
        // 1. Resolver folder_id final en BD (crea subcarpetas si hace falta)
        const finalFolderId = await resolveFolderId(e.folderPath, rootFolderId, folderCache);

        // 2. Extraer archivo del ZIP
        const blob = await e.zipEntry.async('blob');
        const ext  = e.filename.split('.').pop()?.toLowerCase() || '';
        const file = new File([blob], e.filename, { type: getMime(ext) });

        // 3. Llamar a la Edge Function — solo folder_id, sin sub_folder_path
        const fd = new FormData();
        fd.append('file', file);
        fd.append('case_id', caseData.id);
        fd.append('office_id', office.id);
        fd.append('fileName', e.filename);
        fd.append('created_by', currentUser?.id || '');
        if (finalFolderId) fd.append('folder_id', finalFolderId);

        const res    = await fetch(`${supabase.supabaseUrl}/functions/v1/document-upload-google-drive`,
          { method:'POST', headers:{ Authorization:`Bearer ${session?.access_token}` }, body: fd });
        const result = await res.json();
        if (!res.ok || !result.success) throw new Error(result.error || `HTTP ${res.status}`);

        setStatus(p => ({ ...p, [e.path]:'success' }));
        ok++;
      } catch (err) {
        console.error(err);
        setStatus(p => ({ ...p, [e.path]:'error' }));
        e.errorMsg = err.message;
        fail++;
      }
      setProgress({ done:i+1, total: allowed.length });
    }

    setUploading(false); setDone(true);
    if (onComplete) await onComplete();
    if (fail > 0) setWarning(`${fail} archivo(s) fallaron. ${ok} subidos correctamente.`);
    else setWarning('');
  };

  // ── Reset / Close ─────────────────────────────────────────────────────────

  const reset = () => {
    setEntries(null); setPreview(null); setStatus({}); setProgress({ done:0, total:0 });
    setWarning(''); setDone(false); setZipName(''); setRootFolderId(null);
  };
  const handleClose = () => { if (uploading) return; reset(); onClose(); };

  const allowed = entries ? entries.filter(e => e.allowed).length : 0;
  const pct     = progress.total > 0 ? Math.round((progress.done/progress.total)*100) : 0;
  const successes = Object.values(status).filter(s=>s==='success').length;
  const errors    = Object.values(status).filter(s=>s==='error').length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx:{ minHeight:480 } }}>
      <DialogTitle sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', pb:1 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <ZipIcon sx={{ color: officeColor }}/>
          <Typography variant="h6">Subir ZIP con estructura de carpetas</Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={uploading} sx={{ color:'grey.500' }}>
          <CloseIcon/>
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px:3, pb:2 }}>

        {/* ── Drop zone ── */}
        {!entries && (
          <Box onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            sx={{
              border:`2px dashed ${dragActive ? officeColor : '#e0e0e0'}`,
              borderRadius:2, p:4, textAlign:'center', cursor:'pointer',
              backgroundColor: dragActive ? `${officeColor}08` : '#fafafa',
              transition:'all 0.2s', minHeight:180,
              display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
              '&:hover':{ borderColor: officeColor, backgroundColor:`${officeColor}05` }
            }}>
            <input ref={fileRef} type="file" accept=".zip" style={{ display:'none' }} onChange={handleSelect}/>
            <ZipIcon sx={{ fontSize:50, color: dragActive ? officeColor : '#bdbdbd', mb:1.5 }}/>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              {dragActive ? 'Suelta el ZIP aquí' : 'Arrastra un archivo .zip aquí'}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              o <span style={{ color: officeColor, textDecoration:'underline' }}>selecciona desde tu computador</span>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Tipos permitidos: {ALLOWED_EXT.join(', ')} · Máx. 50MB por archivo
            </Typography>
          </Box>
        )}

        {/* ── Vista previa ── */}
        {entries && !done && (
          <>
            {/* Info del ZIP */}
            <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1.5, flexWrap:'wrap' }}>
              <ZipIcon sx={{ color: officeColor, fontSize:19 }}/>
              <Typography variant="subtitle2" fontWeight={700}>{zipName}</Typography>
              <Chip label={`${allowed} archivo(s)`} size="small"
                sx={{ backgroundColor:`${officeColor}15`, color: officeColor }}/>
              {entries.filter(e=>!e.allowed).length > 0 && (
                <Chip label={`${entries.filter(e=>!e.allowed).length} omitido(s)`} size="small"
                  sx={{ backgroundColor:'#fff3e0', color:'#ff9800' }}/>
              )}
              {!uploading && (
                <Button size="small" variant="text" onClick={() => { setEntries(null); setWarning(''); }}
                  sx={{ ml:'auto', color:'text.secondary', fontSize:'0.73rem' }}>
                  Cambiar archivo
                </Button>
              )}
            </Box>

            {/* Selector de carpeta destino */}
            {!uploading && (
              <Box sx={{ mb:2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display:'block', mb:0.75 }}>
                  Carpeta destino en el expediente (el ZIP se extraerá dentro de esta carpeta):
                </Typography>
                <FolderTreeSelect
                  flatFolders={flatFolders}
                  value={rootFolderId}
                  onChange={setRootFolderId}
                  officeColor={officeColor}
                  label="Carpeta destino"
                  size="small"
                />
              </Box>
            )}

            <Divider sx={{ mb:1.5 }}/>

            {/* Árbol del ZIP */}
            <Box sx={{ border:'1px solid #e0e0e0', borderRadius:1, p:1.5,
              maxHeight:260, overflowY:'auto', mb:1.5, backgroundColor:'#fafafa' }}>
              {preview && <PreviewNode node={preview} depth={0} officeColor={officeColor} status={status}/>}
            </Box>

            {/* Barra de progreso */}
            {uploading && (
              <Box sx={{ mb:1.5 }}>
                <Box sx={{ display:'flex', justifyContent:'space-between', mb:0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Subiendo... {progress.done}/{progress.total}
                  </Typography>
                  <Typography variant="caption" fontWeight={700} sx={{ color: officeColor }}>
                    {pct}%
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={pct}
                  sx={{ height:8, borderRadius:4, backgroundColor:'#e0e0e0',
                    '& .MuiLinearProgress-bar':{ backgroundColor: officeColor, borderRadius:4 } }}/>
                <Box sx={{ display:'flex', gap:2, mt:0.5 }}>
                  {successes>0 && <Typography variant="caption" sx={{ color:'#4caf50' }}>✓ {successes} completados</Typography>}
                  {errors>0    && <Typography variant="caption" color="error">✗ {errors} fallidos</Typography>}
                </Box>
              </Box>
            )}
          </>
        )}

        {/* ── Resultado ── */}
        {done && (
          <Box sx={{ textAlign:'center', py:3 }}>
            {errors === 0
              ? <>
                  <CheckCircleIcon sx={{ fontSize:50, color:'#4caf50', mb:1 }}/>
                  <Typography variant="h6" fontWeight={700} color="#2e7d32">¡ZIP subido exitosamente!</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt:0.5 }}>
                    {successes} archivo(s) procesados con su estructura de carpetas.
                  </Typography>
                </>
              : <>
                  <ErrorIcon sx={{ fontSize:50, color:'#ff9800', mb:1 }}/>
                  <Typography variant="h6" fontWeight={700}>{successes} subidos · {errors} fallidos</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt:0.5 }}>Revisa los archivos marcados.</Typography>
                </>
            }
          </Box>
        )}

        {warning && (
          <Alert severity={errors>0 ? 'error' : 'warning'} sx={{ mt:1.5, fontSize:'0.78rem' }}>
            {warning}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px:3, pb:2, gap:1 }}>
        <Button onClick={handleClose} disabled={uploading} variant="outlined">
          {done ? 'Cerrar' : 'Cancelar'}
        </Button>
        {entries && !done && (
          <Button onClick={handleUpload} disabled={uploading || allowed===0} variant="contained"
            startIcon={uploading ? <CircularProgress size={15} color="inherit"/> : <UploadIcon/>}
            sx={{ backgroundColor: officeColor, '&:hover':{ backgroundColor: officeColor, opacity:0.9 },
              '&:disabled':{ backgroundColor:'#cccccc' } }}>
            {uploading ? `Subiendo ${progress.done}/${progress.total}...` : `Subir ${allowed} archivo(s)`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}